/**
 * Run / score a single custom test case for Gate 2 evidence (Save & Execute or Run Once).
 */
import { collectExecutionEvidence, scoreExecutionMetric } from "../../services/gate2ExecutionClient";
import { addTestCase } from "../../services/testCaseClient";
import { toScoreRow } from "./gate2ExecutionScoring";

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Smooth progress toward a cap while async work runs. */
export async function runProgressTicker(setProgress, { from = 0, to = 90, stepMs = 120 } = {}) {
  let current = from;
  setProgress(current);
  while (current < to) {
    await sleep(stepMs);
    current = Math.min(to, current + 4);
    setProgress(current);
  }
}

export function buildCustomScorecardRow({ key, testCaseName, linkedMetricName, scoreData }) {
  const data = scoreData || {};
  return {
    key,
    testCaseName,
    linkedMetricName,
    metricName: testCaseName,
    value: data.value ?? null,
    threshold: data.threshold ?? null,
    direction: data.direction ?? "gte",
    unit: data.unit ?? "fraction",
    passed: data.passed ?? null,
    perCase: data.perCase || [],
    error: data.error || null,
  };
}

/**
 * @param {'runOnce' | 'saveExecute'} mode
 * @param {object} payload Gate 1 add-test-case body
 */
export async function executeCustomTestCase({
  projectId,
  mode,
  payload,
  metrics,
  connectionPayload,
  collectConnectors,
  runId,
  setProgress,
}) {
  const metricRow = metrics.find((m) => m.id === payload.metric_id);
  const catalogMetricId = metricRow?.metricId || null;
  const linkedMetricName = metricRow?.metricName || "Linked metric";
  const testCaseName = payload.name?.trim() || "Custom test case";
  const prompt = Array.isArray(payload.input) ? payload.input[0] : payload.input;

  let testCaseId = null;
  let saveApiResponse = null;

  if (mode === "saveExecute") {
    setProgress(15);
    const res = await addTestCase(projectId, payload);
    if (!res?.success) {
      throw new Error(res?.error || "Failed to save custom test case.");
    }
    saveApiResponse = res;
    const list = res.data?.testCases || [];
    const match = [...list]
      .reverse()
      .find(
        (tc) =>
          tc.source === "Custom" &&
          tc.metricId === payload.metric_id &&
          String(tc.name || "").trim() === testCaseName
      );
    testCaseId = match?.id || list[list.length - 1]?.id;
    setProgress(35);
  } else {
    setProgress(25);
    await sleep(400);
  }

  let actualOutput = "";
  let evidenceCase = {
    id: testCaseId || `ephemeral-${Date.now()}`,
    prompt: prompt || "",
    expected: payload.expected_output || "",
    outcome: "pending",
    chunks: [],
    answer: "",
    evidence: null,
  };

  if (testCaseId && collectConnectors?.length) {
    setProgress(50);
    try {
      const res = await collectExecutionEvidence(projectId, {
        ...connectionPayload,
        ...(runId ? { run_id: runId } : {}),
        test_case_ids: [testCaseId],
      });
      const row = res?.data?.results?.[0];
      if (row) {
        actualOutput = String(row.answer || row.actual_output || "").trim();
        evidenceCase = {
          ...evidenceCase,
          prompt: row.prompt || evidenceCase.prompt,
          outcome: row.outcome || "retrieved",
          chunks: row.chunks || [],
          answer: actualOutput,
          evidence: row.evidence || null,
        };
      }
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === "API_BEARER_REQUIRED" || code === "API_BEARER_REJECTED") {
        const wrapped = new Error(err.response?.data?.error || err.message || "Bearer token required");
        wrapped.code = code;
        throw wrapped;
      }
    }
    setProgress(72);
  } else {
    setProgress(60);
    await sleep(500);
  }

  setProgress(85);
  let scoreData = null;
  if (catalogMetricId) {
    try {
      const scoreRes = await scoreExecutionMetric(projectId, {
        ...(runId ? { run_id: runId } : {}),
        metric_id: catalogMetricId,
        suite_name: testCaseName,
        cases: [
          toScoreRow(
            {
              ...evidenceCase,
              expected: payload.expected_output,
            },
            catalogMetricId
          ),
        ],
      });
      scoreData = scoreRes?.data || scoreRes;
    } catch (err) {
      scoreData = {
        value: null,
        passed: null,
        error: err.response?.data?.error || err.message || "Scoring failed.",
      };
    }
  } else {
    scoreData = { value: null, passed: null, error: "Metric not found in register." };
  }

  setProgress(100);
  await sleep(350);

  const row = buildCustomScorecardRow({
    key: mode === "saveExecute" && testCaseId ? String(testCaseId) : `run-once-${Date.now()}`,
    testCaseName,
    linkedMetricName,
    scoreData,
  });

  return { mode, testCaseId, row, saveApiResponse };
}
